'use client'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { TRPCClientError } from '@trpc/client'
import { ThemeProvider, useTheme } from 'next-themes'
import posthog from 'posthog-js'
import { PostHogProvider as PHProvider } from 'posthog-js/react'
import { useEffect, useState } from 'react'
import { AuthProvider } from '@/contexts/auth-context'
import { getConsent, hasAnalyticsConsent, hasFunctionalConsent } from '@/lib/cookie-consent'
import { createTRPCClient, trpc } from '@/lib/trpc'
import { isProductionAnalyticsHost, posthogPrivacyConfig } from '../../../shared-types/posthog-privacy'
import { ChatwootWidget } from './chatwoot-widget'

const COOKIE_NAME = 'extenshi-theme'
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365

function getThemeCookie(): string | null {
	const m = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`))
	return m ? m[1] : null
}

function setThemeCookie(theme: string) {
	const consent = getConsent()
	if (consent && !consent.functional) return
	const domain = location.hostname.endsWith('.extenshi.io') ? '; domain=.extenshi.io' : ''
	// biome-ignore lint/suspicious/noDocumentCookie: cross-subdomain theme sync requires direct cookie access
	document.cookie = `${COOKIE_NAME}=${theme}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax; Secure${domain}`
}

function clearThemeCookie() {
	const domain = location.hostname.endsWith('.extenshi.io') ? '; domain=.extenshi.io' : ''
	// biome-ignore lint/suspicious/noDocumentCookie: cross-subdomain theme sync requires direct cookie access
	document.cookie = `${COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax; Secure${domain}`
}

function ThemeCookieSync() {
	const { theme, setTheme } = useTheme()

	// biome-ignore lint/correctness/useExhaustiveDependencies: mount-only effect to sync cookie → theme once on load
	useEffect(() => {
		const cookie = getThemeCookie()
		if (cookie && cookie !== theme && /^(dark|light|system)$/.test(cookie)) {
			setTheme(cookie)
		}
	}, [])

	useEffect(() => {
		if (theme) setThemeCookie(theme)
	}, [theme])

	return null
}

function initPostHog() {
	if (typeof window !== 'undefined' && !isProductionAnalyticsHost(window.location.hostname)) return
	if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_POSTHOG_KEY && !posthog.__loaded) {
		posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
			...posthogPrivacyConfig,
			api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com',
			// When api_host is our managed reverse proxy (internal.extenshi.io),
			// ui_host points the toolbar / session-replay links at the real
			// PostHog app (EU). Harmless when api_host is the direct EU host.
			ui_host: 'https://eu.posthog.com',
			capture_pageview: 'history_change',
			// Exception autocapture hooks window.onerror at init time. DO NOT move
			// init() outside the hasAnalyticsConsent() gate above without first
			// adding a consent check here — otherwise we capture exceptions from
			// users who declined analytics.
			capture_exceptions: true,
			cross_subdomain_cookie: true,
			persistence: 'localStorage+cookie',
			loaded: (posthog) => {
				if (process.env.NODE_ENV === 'development') posthog.debug()
				window.dispatchEvent(new Event('posthog-initialized'))
			},
		})
	}
}

export function Providers({ children, chat = true }: { children: React.ReactNode; chat?: boolean }) {
	const [queryClient] = useState(
		() =>
			new QueryClient({
				defaultOptions: {
					queries: {
						// Don't retry auth errors — auth-context refreshes the Genkan JWT
						// refresh asynchronously, so a 401 today will resolve itself on the
						// next natural refetch once cookies are updated. Retrying immediately
						// only piles on more requests against an already-stale token.
						retry: (failureCount, error) => {
							if (
								error instanceof TRPCClientError &&
								(error.data?.code === 'UNAUTHORIZED' || error.data?.httpStatus === 401)
							) {
								return false
							}
							return failureCount < 3
						},
					},
				},
			}),
	)
	const [trpcClient] = useState(() => createTRPCClient())

	useEffect(() => {
		if (hasAnalyticsConsent()) {
			initPostHog()
		}

		function onConsentUpdate() {
			if (hasAnalyticsConsent()) {
				initPostHog()
				if (posthog.__loaded) posthog.opt_in_capturing()
			} else if (posthog.__loaded) {
				// Consent withdrawn mid-session (e.g. "Reject All" after a prior
				// accept): stop capturing and drop PostHog's cookies/storage so the
				// rejection takes effect immediately, not just on next page load.
				posthog.opt_out_capturing()
				posthog.reset()
			}

			// Functional consent withdrawn: remove the theme cookie that was set
			// under it so withdrawal is as effective as never having consented.
			if (!hasFunctionalConsent()) clearThemeCookie()
		}

		window.addEventListener('cookie-consent-updated', onConsentUpdate)
		return () => window.removeEventListener('cookie-consent-updated', onConsentUpdate)
	}, [])

	return (
		<ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
			<ThemeCookieSync />
			<PHProvider client={posthog}>
				<trpc.Provider client={trpcClient} queryClient={queryClient}>
					<QueryClientProvider client={queryClient}>
						<AuthProvider>
							{chat && <ChatwootWidget />}
							{children}
						</AuthProvider>
					</QueryClientProvider>
				</trpc.Provider>
			</PHProvider>
		</ThemeProvider>
	)
}
